import { useEffect, useMemo, useState } from "react";
import { Formik, FieldArray } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CoachMenuLayout from "../../../layouts/CoachMenuLayout";
import Loader from "../../../components/loader";
import { coachPaths } from "../../../routes/coach";
import {
  fetchCoachProfileData,
  updateCoachProfileExtended,
} from "../../../redux/features/coach/actions/coachAction";
import { fetchCoachCategories } from "../../../redux/features/coach/actions/authAction";

const defaultProfileImage = "/assets/img/user_profile.png";

const parseList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
};

const normalizeDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().slice(0, 10);
};

const timezoneOptions = ["UTC", "GMT", "PST", "MST", "CST", "EST", "IST"];

const EditCoachProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading2, coachProfile } = useSelector((state) => state.coach.coach);
  const { categoryList } = useSelector((state) => state.coach.auth);
  const coach = coachProfile?.coach || {};

  useEffect(() => {
    dispatch(fetchCoachProfileData());
    dispatch(fetchCoachCategories());
  }, [dispatch]);

  const initialValues = useMemo(
    () => ({
      full_name: coachProfile?.full_name || "",
      email: coachProfile?.email || "",
      phone_number: coachProfile?.phone_number || "",
      category_id: coach?.category_id || "",
      format: coach?.format || "",
      tagline: coach?.tagline || "",
      bio: coach?.bio || "",
      reason: coach?.reason || "",
      year_of_experience: coach?.year_of_experience || "",
      coaching_approach: coach?.coaching_approach || "",
      coaching_type: parseList(coach?.coaching_type).join(", "),
      country: coach?.country || "",
      city: coach?.city || "",
      state: coach?.state || "",
      address_1: coach?.address_1 || "",
      zip_code: coach?.zip_code || "",
      time_zone: coach?.time_zone || coachProfile?.time_zone || "",
      language: coach?.language || coachProfile?.language || "",
      experiences: parseList(
        coach?.experiences && coach?.experiences?.length
          ? coach?.experiences
          : coachProfile?.experiences
      ).map((exp) => ({
        ...exp,
        start_date: normalizeDate(exp.start_date),
        end_date: normalizeDate(exp.end_date),
      })),
      profile_image_preview: coachProfile?.profile_image || defaultProfileImage,
      profile_image: null,
      cv: null,
      cv_preview: parseList(coach?.cv),
      certificate: null,
      certificate_preview: parseList(coach?.certificate),
    }),
    [coachProfile, coach]
  );

  const handleSubmit = (values) => {
    const formData = new FormData();
    formData.append("full_name", values.full_name || "");
    formData.append("phone_number", values.phone_number || "");
    formData.append("category_id", values.category_id || "");
    formData.append("format", values.format || "");
    formData.append("tagline", values.tagline || "");
    formData.append("bio", values.bio || "");
    formData.append("reason", values.reason || "");
    formData.append("year_of_experience", values.year_of_experience || "");
    formData.append("coaching_approach", values.coaching_approach || "");
    formData.append("coaching_type", values.coaching_type || "");
    formData.append("country", values.country || "");
    formData.append("city", values.city || "");
    formData.append("state", values.state || "");
    formData.append("address_1", values.address_1 || "");
    formData.append("zip_code", values.zip_code || "");
    formData.append("time_zone", values.time_zone || "");
    formData.append("language", values.language || "");
    formData.append("experiences", JSON.stringify(values.experiences || []));

    if (values.profile_image instanceof File) {
      formData.append("profile_image", values.profile_image);
    }
    if (values.cv instanceof File) {
      formData.append("cv", values.cv);
    }
    if (values.certificate instanceof File) {
      formData.append("certificate", values.certificate);
    }

    const callback = (response) => {
      if (!response?.success) return;
      dispatch(fetchCoachProfileData());
      navigate(coachPaths.Profile);
    };

    dispatch(updateCoachProfileExtended({ payload: formData, callback }));
  };

  if (isLoading2) {
    return <Loader />;
  }

  return (
    <CoachMenuLayout>
      <div className="ct_px_40 my-5">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="ct_fs_20_new ct_fw_600 ct_darkblue_text">Edit Coach Profile</h4>
            <p className="mb-0 ct_grey_text">Update your coach profile details</p>
          </div>
          <button
            className="ct_yellow_btn ct_outline_grey ct_h_44"
            type="button"
            onClick={() => navigate(coachPaths.Profile)}
          >
            Back to Profile
          </button>
        </div>

        <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
          {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 mb-4">
                <p className="ct_fs_16 mb-3 ct_darkblue_text">Profile Image</p>
                <div className="d-flex align-items-center gap-3 ct_flex_col_575">
                  <div className="ct_profile_pic ct_flex_shrink_0">
                    <img src={values.profile_image_preview || defaultProfileImage} alt="Profile" />
                  </div>
                  <div className="ct_flex_1">
                    <label className="ct_upload_btn">
                      Upload Profile Photo
                      <input
                        className="ct_file_input"
                        type="file"
                        accept="image/*"
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0];
                          if (file) {
                            const previewUrl = URL.createObjectURL(file);
                            setFieldValue("profile_image", file);
                            setFieldValue("profile_image_preview", previewUrl);
                          }
                        }}
                      />
                    </label>
                    <p className="ct_fs_12 ct_grey_text mb-0">Upload a clear photo.</p>
                  </div>
                </div>
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 mb-4">
                <p className="ct_fs_16 mb-3 ct_darkblue_text">Basic Information</p>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Full Name</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="full_name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.full_name}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Email</label>
                      <input
                        type="email"
                        className="form-control ct_input"
                        value={values.email}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Phone</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="phone_number"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.phone_number}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Category</label>
                      <div className="d-flex flex-column gap-2 position-relative">
                        <select
                          className="form-control ct_input"
                          id="category_id"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.category_id}
                        >
                          <option value="">Select category</option>
                          {(categoryList || []).map((cat) => (
                            <option key={cat.category_id} value={cat.category_id}>
                              {cat.category_name}
                            </option>
                          ))}
                        </select>
                        <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Format</label>
                      <div className="d-flex flex-column gap-2 position-relative">
                        <select
                          className="form-control ct_input"
                          id="format"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.format}
                        >
                          <option value="">Select Coaching Format</option>
                          <option value="1:1">1:1</option>
                          <option value="GROUP">Group</option>
                          <option value="BOTH">Both</option>
                        </select>
                        <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Tagline</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="tagline"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.tagline}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Experience (Years)</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="year_of_experience"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.year_of_experience}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Coaching Approach</label>
                      <textarea
                        rows="3"
                        className="form-control ct_input h-auto"
                        id="coaching_approach"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.coaching_approach}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Coaching Type</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="coaching_type"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.coaching_type}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Bio</label>
                      <textarea
                        rows="4"
                        className="form-control ct_input h-auto"
                        id="bio"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.bio}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Reason</label>
                      <textarea
                        rows="3"
                        className="form-control ct_input h-auto"
                        id="reason"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.reason}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 mb-4">
                <p className="ct_fs_16 mb-3 ct_darkblue_text">Location & Preferences</p>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Country</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="country"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.country}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">State</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="state"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.state}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">City</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="city"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.city}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">ZIP Code</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="zip_code"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.zip_code}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Address</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="address_1"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.address_1}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Time Zone</label>
                      <div className="d-flex flex-column gap-2 position-relative">
                        <select
                          className="form-control ct_input"
                          id="time_zone"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.time_zone}
                        >
                          <option value="">Select Time Zone</option>
                          {timezoneOptions.map((zone) => (
                            <option key={zone} value={zone}>
                              {zone}
                            </option>
                          ))}
                        </select>
                        <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Language</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="language"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.language}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 mb-4">
                <p className="ct_fs_16 mb-3 ct_darkblue_text">Experience</p>
                <FieldArray
                  name="experiences"
                  render={(arrayHelpers) => (
                    <div className="d-flex flex-column gap-3">
                      {(values.experiences || []).map((exp, index) => (
                        <div key={index} className="ct_outline_bg ct_border_radius_10 ct_p_20">
                          <div className="row">
                            <div className="col-md-6">
                              <input
                                className="form-control ct_input mb-3"
                                placeholder="Job Title"
                                name={`experiences.${index}.job_title`}
                                value={exp.job_title || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6">
                              <input
                                className="form-control ct_input mb-3"
                                placeholder="Company Name"
                                name={`experiences.${index}.company_name`}
                                value={exp.company_name || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6">
                              <input
                                type="date"
                                className="form-control ct_input mb-3"
                                name={`experiences.${index}.start_date`}
                                value={exp.start_date || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-md-6">
                              <input
                                type="date"
                                className="form-control ct_input mb-3"
                                name={`experiences.${index}.end_date`}
                                value={exp.end_date || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-12">
                              <textarea
                                rows="2"
                                className="form-control ct_input h-auto mb-3"
                                placeholder="Job Description"
                                name={`experiences.${index}.job_description`}
                                value={exp.job_description || ""}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="col-12">
                              <input
                                type="url"
                                className="form-control ct_input mb-3"
                                placeholder="Link"
                                name={`experiences.${index}.link`}
                                value={exp.link || ""}
                                onChange={handleChange}
                                inputMode="url"
                                pattern="https?://.*"
                                title="Please enter a valid URL (starting with http:// or https://)"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            className="ct_outline_btn ct_h_38"
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="ct_outline_btn ct_h_44"
                        onClick={() =>
                          arrayHelpers.push({
                            job_title: "",
                            company_name: "",
                            start_date: "",
                            end_date: "",
                            currently_working: false,
                            job_description: "",
                            link: "",
                          })
                        }
                      >
                        Add Experience
                      </button>
                    </div>
                  )}
                />
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 mb-4">
                <div className="row">
                  <div className="col-md-6 mb-4 mb-md-0">
                    <p className="ct_fs_16 mb-3 ct_darkblue_text">Upload CV</p>
                    {values.cv_preview?.length ? (
                      <div className="d-flex flex-wrap gap-3 mb-3">
                        {values.cv_preview.map((file, index) => (
                          <div key={`${file}-${index}`} style={{ width: "200px" }}>
                            <div className="ct_single_uploaded_img">
                              <img src={file} alt={`CV ${index + 1}`} />
                            </div>
                            {/* <a
                              className="ct_yellow_btn ct_h_32 ct_white_nowrap mt-2"
                              style={{ width: "100%", textAlign: "center" }}
                              href={file}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View CV {index + 1}
                            </a> */}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <label className="ct_upload_btn">
                      Choose File
                      <input
                        type="file"
                        className="ct_file_input"
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0];
                          if (file) {
                            setFieldValue("cv", file);
                            setFieldValue("cv_preview", [URL.createObjectURL(file)]);
                          }
                        }}
                      />
                    </label>
                  </div>
                  <div className="col-md-6">
                    <p className="ct_fs_16 mb-3 ct_darkblue_text">Upload Certificate</p>
                    {values.certificate_preview?.length ? (
                      <div className="d-flex flex-wrap gap-3 mb-3">
                        {values.certificate_preview.map((file, index) => (
                          <div key={`${file}-${index}`} style={{ width: "200px" }}>
                            <div className="ct_single_uploaded_img">
                              <img src={file} alt={`Certificate ${index + 1}`} />
                            </div>
                            {/* <a
                              className="ct_yellow_btn ct_h_32 ct_white_nowrap mt-2"
                              style={{ width: "100%", textAlign: "center" }}
                              href={file}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View Certificate {index + 1}
                            </a> */}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <label className="ct_upload_btn">
                      Choose File
                      <input
                        type="file"
                        className="ct_file_input"
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0];
                          if (file) {
                            setFieldValue("certificate", file);
                            setFieldValue("certificate_preview", [URL.createObjectURL(file)]);
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-3">
                <button
                  type="button"
                  className="ct_outline_btn ct_h_44"
                  onClick={() => navigate(coachPaths.Profile)}
                >
                  Cancel
                </button>
                <button type="submit" className="ct_yellow_btn ct_h_44">
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CoachMenuLayout>
  );
};

export default EditCoachProfile;

